import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { twilioApi, type TwilioView } from "../../../api/twilio";
import { PageHeader } from "../../../components/ComingSoon";
import { LoadState } from "../shared";
import { CredentialsCard } from "./twilio/CredentialsCard";
import { AllowlistCard, ProviderCard } from "./twilio/ProviderAllowlist";
import { TestConnectionCard } from "./twilio/TestConnectionCard";
import { TestTextCard } from "./twilio/TestTextCard";

function Webhooks({ v }: { v: TwilioView }) {
  const copy = (s: string) => navigator.clipboard?.writeText(s).then(() => toast.success("Copied"));
  return (
    <div className="glass rounded-[20px] p-5 text-sm" data-testid="twilio-webhooks">
      <p className="text-zinc-300">Paste in Twilio → Messaging → Services → your service → <strong className="text-white">Integration → Incoming Messages → Request URL</strong> (HTTP POST):</p>
      {([["Incoming", v.webhooks.inbound, "inbound"], ["Status callback (set automatically on each text)", v.webhooks.status, "status"]] as const).map(([l, u, k]) => (
        <div key={k} className="mt-2 flex items-center gap-2">
          <span className="w-28 shrink-0 text-xs text-zinc-500 sm:w-auto">{l}</span>
          <code className="flex-1 truncate rounded-md bg-ink px-2 py-1.5 font-mono text-xs text-zinc-200" data-testid={`twilio-webhook-${k}`}>{u}</code>
          <button type="button" onClick={() => copy(u)} aria-label={`Copy ${l}`} className="p-1 text-zinc-400 hover:text-white" data-testid={`twilio-copy-${k}`}><Copy className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

export default function TwilioConnection() {
  const [v, setV] = useState<TwilioView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { twilioApi.get().then(setV).catch((e) => setError(errorMessage(e))); }, []);
  return (
    <div data-testid="admin-twilio-connection-page" className="animate-rise space-y-6">
      <Link to="/admin/settings" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="twilio-connection-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Settings</Link>
      <PageHeader title="Twilio connection" />
      <LoadState error={error} loading={!v && !error} testId="twilio-connection-load" />
      {v && (
        <>
          <p className="max-w-3xl text-sm text-zinc-400" data-testid="twilio-summary">
            {v.environment === "preview" ? "Preview" : "Production"} · texts are <strong className="text-white" data-testid="twilio-summary-mode">{v.provider_mode === "twilio" ? "sent with real Twilio" : v.provider_mode === "mock" ? "simulated (mock outbox)" : "turned off"}</strong>
            {" "}· credentials {v.credential_source === "env" ? "from Secrets" : v.credential_source === "page" ? "saved on this page" : "not set"}. Values in Secrets always win over this page. <Link to="/admin/settings/twilio-guide" className="text-signal hover:text-white" data-testid="twilio-guide-link">Setup guide →</Link>
          </p>
          <CredentialsCard v={v} onChange={setV} />
          <TestConnectionCard v={v} onChange={setV} />
          <Webhooks v={v} />
          {v.environment === "preview" && <ProviderCard v={v} onChange={setV} />}
          {v.environment === "preview" && <AllowlistCard v={v} onChange={setV} />}
          <TestTextCard key={v.allowlist.map((a) => a.phone_e164).join()} v={v} />
        </>
      )}
    </div>
  );
}
